import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  type ShouldRevalidateFunctionArgs,
  useLocation,
  useNavigate,
} from 'react-router';
import { useState, useEffect, useMemo, useCallback } from 'react';

const loginPath = '/user/login';

import type { Route } from './+types/root';
import '@mantine/core/styles.layer.css';
import '@mantine/notifications/styles.layer.css';
import './app.css';
import { client } from './services/client';
import getAccess, { CurrentUserContext } from './components/CurrentUserContext';
import { LowDataModeProvider } from './components/LowDataModeContext';
import { OwnedFilmsProvider } from './components/OwnedFilmsContext';
import { TagsContext, type Tag } from './components/TagsContext';
import { loadTags, buildAliasMap, loadLabelTagIds } from './services/tag';
import { setLabelTagIds, isTagPagePath } from './utils/tagHref';
import { subscribeTagsChanged } from './services/tagsSync';
import type { User } from 'backend/client';
import {
  ColorSchemeScript,
  Input,
  MantineProvider,
  createTheme,
  mantineHtmlProps,
} from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import Settings from './defaultSettings';
import GoogleTagManager from './components/GoogleTagManager';
import { isbot } from 'isbot';
import {
  CONSENT_PRECHECK_SCRIPT,
  SimpleConsentBanner,
  getAnalyticsConsent,
} from './components/SimpleConsentBanner';
import { initializeConsentBridge, updateConsentStatus } from './utils/consentBridge';
import { readLowDataModeFromCookieHeader } from './utils/lowDataModeCookie';

const theme = createTheme({
  breakpoints: {
    xs: '36em',
    sm: '48em',
    md: '62em',
    lg: '75em',
    xl: '88em',
    xxl: '100em',
  },
  cursorType: 'pointer',
  respectReducedMotion: true,
  components: {
    List: {
      defaultProps: {
        listStyleType: 'circle',
      },
    },
    Anchor: {
      defaultProps: {
        underline: 'hover',
      },
    },
    Card: {
      defaultProps: {
        shadow: 'xs',
        p: 24,
        radius: 'md',
      },
    },
    Title: {
      defaultProps: {
        order: 4,
        mb: 10,
        fw: 600,
      },
    },
    Input: Input.extend({
      styles: {
        input: {
          backgroundColor: 'light-dark(white, #141414)',
        },
      },
    }),
  },
});

export const links: Route.LinksFunction = () => [
  {
    rel: 'search',
    type: 'application/opensearchdescription+xml',
    title: 'Best Blurays',
    href: '/opensearch.xml',
  },
  // Preconnect to TMDB image CDN — all poster images use this host; saves ~330ms on LCP
  { rel: 'preconnect', href: 'https://media.themoviedb.org' },
  // DNS-prefetch for image.tmdb.org (og:image tags only; no preconnect needed)
  { rel: 'dns-prefetch', href: 'https://image.tmdb.org' },
];

export const meta: Route.MetaFunction = () => {
  return [
    { title: Settings.title },
    { property: 'og:title', content: Settings.title },
    { name: 'application-name', content: Settings.title },
    {
      name: 'description',
      content: Settings.description,
    },
    {
      property: 'og:description',
      content: Settings.description,
    },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: 'https://www.bestblurays.com' },
    { property: 'og:image', content: 'https://www.bestblurays.com/icons/icon-512x512.png' },
    { property: 'og:image:width', content: '512' },
    { property: 'og:image:height', content: '512' },
    { property: 'og:image:alt', content: 'Best Blurays Project Logo' },
  ];
};

// Server loader provides default anonymous state so SSR renders full page content.
// Auth is handled client-side in the App component via useEffect.
// lowDataMode is read from a cookie so SSR renders the correct image tier
// (avoids fetching high-res then swapping to low-res after hydration).
// isBot keeps the consent banner out of crawler HTML (it is server-rendered for
// everyone else — see CONSENT_PRECHECK_SCRIPT).
export const loader = async ({ request }: { request: Request }) => {
  const lowDataMode = readLowDataModeFromCookieHeader(request.headers.get('cookie'));
  const isBot = isbot(request.headers.get('user-agent'));
  // Label ids decide whether a tag mention links to /labels/<id> or /tag/<id>.
  // Registered here (server) and again in App (client) so SSR and hydration agree.
  const labelIds = await loadLabelTagIds();
  setLabelTagIds(labelIds);
  return { currentUser: undefined, lowDataMode, isBot, labelIds };
};

export const shouldRevalidate = ({
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs) => {
  // Only revalidate the root loader on initial hydration or when path stays the same
  // This prevents unnecessary auth checks on every navigation
  if (currentUrl.pathname !== nextUrl.pathname) {
    return false;
  }
  return defaultShouldRevalidate;
};

export function Layout({ children }: { children: React.ReactNode }) {
  const gtmContainerId = import.meta.env.VITE_GTM_CONTAINER_ID;
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  return (
    <html lang="en" {...mantineHtmlProps}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {/* Establish cascade layer order before any stylesheet is parsed. Mantine's
            `styles.layer.css` files all emit `@layer mantine`, and app.css wraps its
            global reset in `@layer reset` — everything else we write stays unlayered
            and therefore always beats Mantine regardless of <link> order. That matters
            because React Router appends `#` to manifest CSS URLs reachable via dynamic
            import, which defeats Vite's preload-helper dedupe and makes it append a
            second `mantine-vendor.css` link at the end of <head> on some routes. */}
        <style dangerouslySetInnerHTML={{ __html: '@layer reset, mantine;' }} />
        <script dangerouslySetInnerHTML={{ __html: CONSENT_PRECHECK_SCRIPT }} />
        <meta property="og:site_name" content={Settings.title} />
        <ColorSchemeScript defaultColorScheme="auto" />
        <Meta />
        <Links />
        {isHomePage && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'WebSite',
                name: 'Best Blurays',
                alternateName: ['BestBlurays', 'bestblurays.com'],
                url: 'https://www.bestblurays.com/',
                description: Settings.description,
                image: 'https://www.bestblurays.com/icons/icon-512x512.png',
                potentialAction: {
                  '@type': 'SearchAction',
                  target: {
                    '@type': 'EntryPoint',
                    urlTemplate: 'https://www.bestblurays.com/films/?title={search_term_string}',
                  },
                  'query-input': 'required name=search_term_string',
                },
              }),
            }}
          />
        )}
      </head>
      <body>
        {gtmContainerId && (
          <noscript>
            <iframe
              src={`https://www.googletagmanager.com/ns.html?id=${gtmContainerId}`}
              height="0"
              width="0"
              style={{ display: 'none', visibility: 'hidden' }}
            />
          </noscript>
        )}
        <MantineProvider theme={theme} defaultColorScheme="auto">
          {children}
        </MantineProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App({ loaderData }: Route.ComponentProps) {
  // Must run before children render (see utils/tagHref.ts); it's idempotent.
  useMemo(() => setLabelTagIds(loaderData.labelIds ?? []), [loaderData.labelIds]);
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<User | undefined>(loaderData.currentUser);
  const [analyticsConsent, setAnalyticsConsent] = useState<boolean>(false);
  const [tags, setTags] = useState<Tag[]>([]);
  const [tagsLoading, setTagsLoading] = useState<boolean>(true);

  // Analytics configuration from environment variables
  const analyticsConfig = {
    gtm: {
      enabled: import.meta.env.VITE_GTM_ENABLED === 'true' || false,
      containerId: import.meta.env.VITE_GTM_CONTAINER_ID,
    },
  };

  // Re-authenticate on mount (replaces former clientLoader).
  // SSR renders with currentUser=undefined; after hydration, this checks for a stored JWT.
  useEffect(() => {
    if (window.location.pathname.endsWith(loginPath)) return;
    client
      .reAuthenticate()
      .then((res) => setCurrentUser(res.user))
      .catch(() => {});
  }, []);

  // Initialize consent bridge and check existing consent on mount
  useEffect(() => {
    initializeConsentBridge();
    setAnalyticsConsent(getAnalyticsConsent());
  }, []);

  const refreshTags = useCallback(async () => {
    setTagsLoading(true);
    const isNotAuthenticated = (error: any) =>
      error?.name === 'NotAuthenticated' || error?.message?.includes('NotAuthenticated');
    try {
      let tagsArray: Tag[];
      try {
        tagsArray = await loadTags();
      } catch (error: any) {
        // Tags are public. A NotAuthenticated here is the mount-time race: the
        // call waits on the in-flight reAuthenticate(), and for an anonymous
        // visitor that rejects. Once it has settled the plain call succeeds,
        // so retry once — otherwise anonymous users never get tags and the
        // release filters sit on an empty tag list.
        if (!isNotAuthenticated(error)) throw error;
        tagsArray = await loadTags();
      }
      setTags(tagsArray);
      // Picks up labels created or retyped since the page loaded.
      setLabelTagIds(tagsArray.filter((t) => t.type === 'label').map((t) => t.id));
    } catch (error: any) {
      if (!isNotAuthenticated(error)) {
        console.error('Error loading tags:', error);
      }
      setTags((prev) => (prev.length ? [] : prev));
    } finally {
      setTagsLoading(false);
    }
  }, []);

  // Load tags on mount AND whenever auth state changes.
  // Tags are public, so anonymous visitors get them too (see the retry in
  // refreshTags). When the user logs in (or
  // reAuthenticate resolves), currentUser?.id changes and we refetch with a
  // valid JWT, so tags appear without needing a page refresh.
  useEffect(() => {
    refreshTags();
  }, [refreshTags, currentUser?.id]);

  // Sync tags across tabs: when another tab creates/edits/deletes a tag, it
  // broadcasts a change signal and this tab refetches once. Event-driven, so
  // there's no idle cost — work only happens on an actual mutation elsewhere.
  useEffect(() => {
    return subscribeTagsChanged(refreshTags);
  }, [refreshTags]);

  // Set up global click handler for tag mention links
  useEffect(() => {
    const handleTagLinkClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const tagLink = target.closest('a.hashtag-mention-link');
      if (tagLink) {
        // Check if already handled (prevent duplicate opens)
        if (tagLink.getAttribute('data-handled') === 'true') {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          return;
        }

        const href = tagLink.getAttribute('href');
        if (href && isTagPagePath(href)) {
          // Check if we're inside an editor (tiptap editor) FIRST
          const isInEditor = tagLink.closest(
            '.tiptap, .mantine-RichTextEditor-content, [contenteditable="true"]',
          );

          // Mark as handled immediately to prevent duplicate handling
          tagLink.setAttribute('data-handled', 'true');

          // Always prevent default to avoid browser navigation
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          if (isInEditor) {
            // In editor: open in new tab only, do NOT navigate
            window.open(href, '_blank', 'noopener,noreferrer');
            // Clear the handled flag after a short delay
            setTimeout(() => {
              tagLink.removeAttribute('data-handled');
            }, 100);
            return; // Early return - don't do anything else
          } else {
            // Not in editor: navigate in current page
            navigate(href);
            // Clear the handled flag after a short delay
            setTimeout(() => {
              tagLink.removeAttribute('data-handled');
            }, 100);
          }
        }
      }
    };

    // Use capture phase to catch event early, before other handlers
    document.addEventListener('click', handleTagLinkClick, true);
    return () => {
      document.removeEventListener('click', handleTagLinkClick, true);
    };
  }, [navigate]);

  // Build alias map from tags
  const aliasMap = useMemo(() => {
    return buildAliasMap(tags);
  }, [tags]);

  // Handle consent changes from banner
  const handleConsentChange = (hasConsent: boolean) => {
    setAnalyticsConsent(hasConsent);
    updateConsentStatus(hasConsent);
  };

  // Stable context values: the films route and every FilmCard consume these, so a
  // fresh object per App render (tags load, consent state) re-rendered the whole
  // list after hydration.
  const currentUserValue = useMemo(
    () => ({ currentUser, setCurrentUser, access: getAccess(currentUser) }),
    [currentUser],
  );
  const tagsValue = useMemo(
    () => ({ tags, aliasMap, loading: tagsLoading, refreshTags }),
    [tags, aliasMap, tagsLoading, refreshTags],
  );

  return (
    <CurrentUserContext value={currentUserValue}>
      <OwnedFilmsProvider>
        <LowDataModeProvider initialValue={loaderData.lowDataMode}>
          <TagsContext.Provider value={tagsValue}>
            <GoogleTagManager config={analyticsConfig.gtm} consentGiven={analyticsConsent} />
            <SimpleConsentBanner
              onConsentChange={handleConsentChange}
              initiallyVisible={!loaderData.isBot}
            />
            <Notifications position="top-right" />
            <Outlet />
          </TagsContext.Provider>
        </LowDataModeProvider>
      </OwnedFilmsProvider>
    </CurrentUserContext>
  );
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = 'Oops!';
  let details = 'An unexpected error occurred.';
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? '404' : 'Error';
    details =
      error.status === 404 ? 'The requested page could not be found.' : error.statusText || details;
  } else if (error && error instanceof Error) {
    import('@sentry/react-router').then(({ captureException }) => captureException(error));
    if (import.meta.env.DEV) {
      details = error.message;
      stack = error.stack;
    }
  }

  return (
    <main className="pt-16 p-4 container mx-auto">
      <h1>{message}</h1>
      <p>{details}</p>
      {stack && (
        <pre className="w-full p-4 overflow-x-auto">
          <code>{stack}</code>
        </pre>
      )}
    </main>
  );
}
